<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Preventive Services Grid -->
      <div class="preventive-services-grid">
        
        <!-- Tetanus Toxoid Table -->
        <div class="service-card">
          <div class="service-header">
            <h3><i class="fas fa-syringe"></i> Tetanus Toxoid</h3>
            <button class="btn-add" @click="showTTModal = true">
              <i class="fas fa-plus"></i> Add Record
            </button>
          </div>
          <div class="service-body">
            <table class="service-table">
              <thead>
                <tr>
                  <th>Dose</th>
                  <th>Date Given</th>
                  <th>Gestation</th>
                  <th>Given By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in tetanusRecords" :key="record.id">
                  <td>
                    <span class="dose-badge" :class="getDoseClass(record.dose)">
                      {{ record.dose }}
                    </span>
                  </td>
                  <td>{{ formatDate(record.dateGiven) }}</td>
                  <td>{{ record.gestation }} wks</td>
                  <td>{{ record.givenBy }}</td>
                  <td class="actions">
                    <button class="btn-action edit" @click="editTTRecord(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-action delete" @click="deleteTTRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="tetanusRecords.length === 0">
                  <td colspan="5" class="text-center text-muted">
                    No tetanus records found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Iron & Folic Acid Table -->
        <div class="service-card">
          <div class="service-header">
            <h3><i class="fas fa-pills"></i> Iron & Folic Acid</h3>
            <button class="btn-add" @click="showIronModal = true">
              <i class="fas fa-plus"></i> Add Record
            </button>
          </div>
          <div class="service-body">
            <table class="service-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Gestation</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in ironRecords" :key="record.id">
                  <td>{{ formatDate(record.date) }}</td>
                  <td>{{ record.gestation }} wks</td>
                  <td>{{ record.type }}</td>
                  <td>{{ record.quantity }}</td>
                  <td class="actions">
                    <button class="btn-action edit" @click="editIronRecord(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-action delete" @click="deleteIronRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="ironRecords.length === 0">
                  <td colspan="5" class="text-center text-muted">
                    No iron supplementation records
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- IPTp (Malaria Prevention) Table -->
        <div class="service-card">
          <div class="service-header">
            <h3><i class="fas fa-mosquito"></i> IPTp (Malaria)</h3>
            <button class="btn-add" @click="showIPTpModal = true">
              <i class="fas fa-plus"></i> Add Record
            </button>
          </div>
          <div class="service-body">
            <table class="service-table">
              <thead>
                <tr>
                  <th>Dose</th>
                  <th>Date Given</th>
                  <th>Gestation</th>
                  <th>Drug</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in iptpRecords" :key="record.id">
                  <td>
                    <span class="dose-badge" :class="getIPTpDoseClass(record.dose)">
                      {{ record.dose }}
                    </span>
                  </td>
                  <td>{{ formatDate(record.dateGiven) }}</td>
                  <td>{{ record.gestation }} wks</td>
                  <td>{{ record.drug }}</td>
                  <td class="actions">
                    <button class="btn-action edit" @click="editIPTpRecord(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-action delete" @click="deleteIPTpRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="iptpRecords.length === 0">
                  <td colspan="5" class="text-center text-muted">
                    No IPTp records found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Deworming Table -->
        <div class="service-card">
          <div class="service-header">
            <h3><i class="fas fa-bug"></i> Deworming</h3>
            <button class="btn-add" @click="showDewormingModal = true">
              <i class="fas fa-plus"></i> Add Record
            </button>
          </div>
          <div class="service-body">
            <table class="service-table">
              <thead>
                <tr>
                  <th>Date Given</th>
                  <th>Gestation</th>
                  <th>Drug</th>
                  <th>Dose</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in dewormingRecords" :key="record.id">
                  <td>{{ formatDate(record.dateGiven) }}</td>
                  <td>{{ record.gestation }} wks</td>
                  <td>{{ record.drug }}</td>
                  <td>{{ record.dose }}</td>
                  <td class="actions">
                    <button class="btn-action edit" @click="editDewormingRecord(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-action delete" @click="deleteDewormingRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="dewormingRecords.length === 0">
                  <td colspan="5" class="text-center text-muted">
                    No deworming records
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>

    <!-- Tetanus Toxoid Modal -->
    <div v-if="showTTModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingTT ? 'Edit' : 'Add' }} Tetanus Toxoid Record</h3>
          <button class="modal-close" @click="closeTTModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveTTRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Dose *</label>
                <select v-model="ttForm.dose" required class="form-control">
                  <option value="">Select Dose</option>
                  <option value="TT1">TT1</option>
                  <option value="TT2">TT2</option>
                  <option value="TT3">TT3</option>
                  <option value="TT4">TT4</option>
                  <option value="TT5">TT5</option>
                </select>
              </div>

              <div class="form-group">
                <label>Date Given *</label>
                <input type="date" v-model="ttForm.dateGiven" required class="form-control">
              </div>

              <div class="form-group">
                <label>Gestation (weeks) *</label>
                <input type="number" v-model="ttForm.gestation" min="4" max="42" required class="form-control">
              </div>

              <div class="form-group">
                <label>Given By *</label>
                <input type="text" v-model="ttForm.givenBy" required class="form-control" placeholder="Doctor/Nurse name">
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeTTModal">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingTT ? 'Update' : 'Save' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Iron & Folic Acid Modal -->
    <div v-if="showIronModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingIron ? 'Edit' : 'Add' }} Iron & Folic Acid Record</h3>
          <button class="modal-close" @click="closeIronModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveIronRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Date *</label>
                <input type="date" v-model="ironForm.date" required class="form-control">
              </div>

              <div class="form-group">
                <label>Gestation (weeks) *</label>
                <input type="number" v-model="ironForm.gestation" min="4" max="42" required class="form-control">
              </div>

              <div class="form-group">
                <label>Type *</label>
                <select v-model="ironForm.type" required class="form-control">
                  <option value="">Select Type</option>
                  <option value="Iron Tablet">Iron Tablet</option>
                  <option value="Folic Acid">Folic Acid</option>
                  <option value="Combination">Combination</option>
                  <option value="IV Iron">IV Iron</option>
                </select>
              </div>

              <div class="form-group">
                <label>Quantity *</label>
                <input type="text" v-model="ironForm.quantity" required class="form-control" placeholder="e.g., 30 tablets">
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeIronModal">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingIron ? 'Update' : 'Save' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- IPTp Modal -->
    <div v-if="showIPTpModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingIPTp ? 'Edit' : 'Add' }} IPTp Record</h3>
          <button class="modal-close" @click="closeIPTpModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveIPTpRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Dose *</label>
                <select v-model="iptpForm.dose" required class="form-control">
                  <option value="">Select Dose</option>
                  <option value="IPTp1">IPTp1</option>
                  <option value="IPTp2">IPTp2</option>
                  <option value="IPTp3">IPTp3</option>
                  <option value="IPTp4">IPTp4</option>
                </select>
              </div>

              <div class="form-group">
                <label>Date Given *</label>
                <input type="date" v-model="iptpForm.dateGiven" required class="form-control">
              </div>

              <div class="form-group">
                <label>Gestation (weeks) *</label>
                <input type="number" v-model="iptpForm.gestation" min="13" max="42" required class="form-control">
              </div>

              <div class="form-group">
                <label>Drug *</label>
                <select v-model="iptpForm.drug" required class="form-control">
                  <option value="">Select Drug</option>
                  <option value="SP (Sulfadoxine-Pyrimethamine)">SP (Sulfadoxine-Pyrimethamine)</option>
                  <option value="DHA-PPQ">DHA-PPQ</option>
                </select>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeIPTpModal">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingIPTp ? 'Update' : 'Save' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Deworming Modal -->
    <div v-if="showDewormingModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingDeworming ? 'Edit' : 'Add' }} Deworming Record</h3>
          <button class="modal-close" @click="closeDewormingModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveDewormingRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Date Given *</label>
                <input type="date" v-model="dewormingForm.dateGiven" required class="form-control">
              </div>

              <div class="form-group">
                <label>Gestation (weeks) *</label>
                <input type="number" v-model="dewormingForm.gestation" min="13" max="42" required class="form-control">
              </div>

              <div class="form-group">
                <label>Drug *</label>
                <select v-model="dewormingForm.drug" required class="form-control">
                  <option value="">Select Drug</option>
                  <option value="Albendazole">Albendazole</option>
                  <option value="Mebendazole">Mebendazole</option>
                </select>
              </div>

              <div class="form-group">
                <label>Dose *</label>
                <input type="text" v-model="dewormingForm.dose" required class="form-control" placeholder="e.g., 400mg">
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeDewormingModal">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingDeworming ? 'Update' : 'Save' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import full_head from "./full_head.vue";

export default {
  name: "PreventiveServices",
  components: {
    full_head,
  },
  data() {
    return {
      // Tetanus Toxoid
      showTTModal: false,
      editingTT: null,
      ttForm: {
        dose: "TT1",
        dateGiven: new Date().toISOString().split("T")[0],
        gestation: 12,
        givenBy: "",
      },
      tetanusRecords: [
        {
          id: 1,
          dose: "TT1",
          dateGiven: "2023-09-01",
          gestation: 12,
          givenBy: "Dr. Jane Smith",
        },
      ],

      // Iron & Folic Acid
      showIronModal: false,
      editingIron: null,
      ironForm: {
        date: new Date().toISOString().split("T")[0],
        gestation: 12,
        type: "Iron Tablet",
        quantity: "30 tablets",
      },
      ironRecords: [],

      // IPTp
      showIPTpModal: false,
      editingIPTp: null,
      iptpForm: {
        dose: "IPTp1",
        dateGiven: new Date().toISOString().split("T")[0],
        gestation: 16,
        drug: "SP (Sulfadoxine-Pyrimethamine)",
      },
      iptpRecords: [],

      // Deworming
      showDewormingModal: false,
      editingDeworming: null,
      dewormingForm: {
        dateGiven: new Date().toISOString().split("T")[0],
        gestation: 16,
        drug: "Albendazole",
        dose: "400mg",
      },
      dewormingRecords: [],
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getDoseClass(dose) {
      const classes = {
        TT1: "dose-tt1",
        TT2: "dose-tt2",
        TT3: "dose-tt3",
        TT4: "dose-tt4",
        TT5: "dose-tt5",
      };
      return classes[dose] || "dose-default";
    },

    getIPTpDoseClass(dose) {
      const classes = {
        IPTp1: "dose-iptp1",
        IPTp2: "dose-iptp2",
        IPTp3: "dose-iptp3",
        IPTp4: "dose-iptp4",
      };
      return classes[dose] || "dose-default";
    },

    // Tetanus Toxoid Methods
    editTTRecord(record) {
      this.editingTT = record;
      this.ttForm = { ...record };
      this.showTTModal = true;
    },

    deleteTTRecord(id) {
      if (confirm("Are you sure you want to delete this tetanus record?")) {
        this.tetanusRecords = this.tetanusRecords.filter(record => record.id !== id);
      }
    },

    closeTTModal() {
      this.showTTModal = false;
      this.editingTT = null;
      this.resetTTForm();
    },

    resetTTForm() {
      this.ttForm = {
        dose: "TT1",
        dateGiven: new Date().toISOString().split("T")[0],
        gestation: 12,
        givenBy: "",
      };
    },

    saveTTRecord() {
      const recordData = {
        ...this.ttForm,
        id: this.editingTT ? this.editingTT.id : Date.now(),
      };

      if (this.editingTT) {
        const index = this.tetanusRecords.findIndex(record => record.id === this.editingTT.id);
        this.tetanusRecords[index] = recordData;
      } else {
        this.tetanusRecords.push(recordData);
      }

      // Sort by date
      this.tetanusRecords.sort((a, b) => new Date(a.dateGiven) - new Date(b.dateGiven));
      
      this.closeTTModal();
    },

    // Iron & Folic Acid Methods
    editIronRecord(record) {
      this.editingIron = record;
      this.ironForm = { ...record };
      this.showIronModal = true;
    },

    deleteIronRecord(id) {
      if (confirm("Are you sure you want to delete this iron supplementation record?")) {
        this.ironRecords = this.ironRecords.filter(record => record.id !== id);
      }
    },

    closeIronModal() {
      this.showIronModal = false;
      this.editingIron = null;
      this.resetIronForm();
    },

    resetIronForm() {
      this.ironForm = {
        date: new Date().toISOString().split("T")[0],
        gestation: 12,
        type: "Iron Tablet",
        quantity: "30 tablets",
      };
    },

    saveIronRecord() {
      const recordData = {
        ...this.ironForm,
        id: this.editingIron ? this.editingIron.id : Date.now(),
      };

      if (this.editingIron) {
        const index = this.ironRecords.findIndex(record => record.id === this.editingIron.id);
        this.ironRecords[index] = recordData;
      } else {
        this.ironRecords.push(recordData);
      }

      // Sort by date
      this.ironRecords.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      this.closeIronModal();
    },

    // IPTp Methods
    editIPTpRecord(record) {
      this.editingIPTp = record;
      this.iptpForm = { ...record };
      this.showIPTpModal = true;
    },

    deleteIPTpRecord(id) {
      if (confirm("Are you sure you want to delete this IPTp record?")) {
        this.iptpRecords = this.iptpRecords.filter(record => record.id !== id);
      }
    },

    closeIPTpModal() {
      this.showIPTpModal = false;
      this.editingIPTp = null;
      this.resetIPTpForm();
    },

    resetIPTpForm() {
      this.iptpForm = {
        dose: "IPTp1",
        dateGiven: new Date().toISOString().split("T")[0],
        gestation: 16,
        drug: "SP (Sulfadoxine-Pyrimethamine)",
      };
    },

    saveIPTpRecord() {
      const recordData = {
        ...this.iptpForm,
        id: this.editingIPTp ? this.editingIPTp.id : Date.now(),
      };

      if (this.editingIPTp) {
        const index = this.iptpRecords.findIndex(record => record.id === this.editingIPTp.id);
        this.iptpRecords[index] = recordData;
      } else {
        this.iptpRecords.push(recordData);
      }

      // Sort by dose number
      this.iptpRecords.sort((a, b) => {
        const aNum = parseInt(a.dose.replace('IPTp', ''));
        const bNum = parseInt(b.dose.replace('IPTp', ''));
        return aNum - bNum;
      });
      
      this.closeIPTpModal();
    },

    // Deworming Methods
    editDewormingRecord(record) {
      this.editingDeworming = record;
      this.dewormingForm = { ...record };
      this.showDewormingModal = true;
    },

    deleteDewormingRecord(id) {
      if (confirm("Are you sure you want to delete this deworming record?")) {
        this.dewormingRecords = this.dewormingRecords.filter(record => record.id !== id);
      }
    },

    closeDewormingModal() {
      this.showDewormingModal = false;
      this.editingDeworming = null;
      this.resetDewormingForm();
    },

    resetDewormingForm() {
      this.dewormingForm = {
        dateGiven: new Date().toISOString().split("T")[0],
        gestation: 16,
        drug: "Albendazole",
        dose: "400mg",
      };
    },

    saveDewormingRecord() {
      const recordData = {
        ...this.dewormingForm,
        id: this.editingDeworming ? this.editingDeworming.id : Date.now(),
      };

      if (this.editingDeworming) {
        const index = this.dewormingRecords.findIndex(record => record.id === this.editingDeworming.id);
        this.dewormingRecords[index] = recordData;
      } else {
        this.dewormingRecords.push(recordData);
      }

      // Sort by date
      this.dewormingRecords.sort((a, b) => new Date(a.dateGiven) - new Date(b.dateGiven));
      
      this.closeDewormingModal();
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.main-content {
  margin-top: 20px;
}

.preventive-services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.service-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.service-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 15px 20px;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.service-header h3 {
  margin: 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
}

.service-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.btn-add {
  padding: 6px 15px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
}

.btn-add:hover {
  background: #218838;
}

.service-body {
  padding: 20px;
  flex: 1;
}

.service-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.service-table th {
  background: #f8f9fa;
  padding: 10px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.service-table td {
  padding: 10px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.dose-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  min-width: 40px;
}

.dose-tt1 { background: #3498db; color: white; }
.dose-tt2 { background: #27ae60; color: white; }
.dose-tt3 { background: #f39c12; color: white; }
.dose-tt4 { background: #9b59b6; color: white; }
.dose-tt5 { background: #e74c3c; color: white; }
.dose-iptp1 { background: #3498db; color: white; }
.dose-iptp2 { background: #27ae60; color: white; }
.dose-iptp3 { background: #f39c12; color: white; }
.dose-iptp4 { background: #9b59b6; color: white; }
.dose-default { background: #95a5a6; color: white; }

.actions {
  display: flex;
  gap: 5px;
}

.btn-action {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.btn-action.edit {
  background: #3498db;
  color: white;
}

.btn-action.edit:hover {
  background: #2980b9;
}

.btn-action.delete {
  background: #e74c3c;
  color: white;
}

.btn-action.delete:hover {
  background: #c0392b;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 15px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

@media (max-width: 1200px) {
  .preventive-services-grid {
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  }
}

@media (max-width: 992px) {
  .preventive-services-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .container-fluid {
    padding: 10px;
  }

  .service-table {
    font-size: 13px;
  }

  .service-table th,
  .service-table td {
    padding: 8px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .service-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .btn-add {
    align-self: stretch;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .service-table {
    display: block;
    overflow-x: auto;
  }
}
</style>